import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, X, RotateCw, Check, Zap, Images, Sparkles } from "lucide-react";

/**
 * Named guided-mode shot templates. When `guided` is passed, retailer
 * walks through these one by one — each with a translucent silhouette
 * hint and a coach caption. The final "label" shot is flagged so the
 * calling component can offer to AI-scan it after capture.
 */
export const GUIDED_SHOTS = [
  {
    key: "roll_end",
    caption: "Shot 1 of 3 — Full roll end",
    hint: "Show the whole roll end so buyers can see the width and colour.",
    frame: "rectangle-wide",
  },
  {
    key: "close_up",
    caption: "Shot 2 of 3 — Pile close-up",
    hint: "Get close to the surface so texture and pile read clearly.",
    frame: "square",
  },
  {
    key: "label",
    caption: "Shot 3 of 3 — Product label",
    hint: "Frame the label straight-on. FloorMatch can read the details for you.",
    frame: "label",
  },
];

/**
 * In-app full-screen camera capture. Mobile-first: live rear-camera preview,
 * big shutter button, multi-shot, retake. Uses `navigator.mediaDevices` so
 * everything stays inside FloorMatch — no OS handoff, no leaving the app.
 *
 * Props:
 *   open          — boolean, controls visibility
 *   onClose()     — called when user dismisses the camera
 *   onCapture(files) — called with an array of File objects (image/jpeg)
 *                     when the user confirms their shots. Files match the
 *                     shape the upload pipeline already expects.
 *   maxShots      — cap on how many photos this session can capture
 *   fallbackToPicker — if true (default), shows a "Pick from gallery"
 *                     button that triggers a hidden <input type="file"
 *                     capture="environment"> so users on unsupported
 *                     browsers still work.
 *
 * Falls back to the native file picker whenever getUserMedia fails
 * (permission denied, no camera, insecure context, older browsers).
 */
export default function CameraCapture({
  open,
  onClose,
  onCapture,
  maxShots = 5,
  fallbackToPicker = true,
  guided = false,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const nativeInputRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [shots, setShots] = useState([]); // [{ dataUrl, file, key? }]
  const [flash, setFlash] = useState(false);
  const guidedLimit = guided ? GUIDED_SHOTS.length : maxShots;
  const currentGuide = guided ? GUIDED_SHOTS[shots.length] : null;

  const stopStream = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setReady(false);
  }, []);

  const startStream = useCallback(async (mode) => {
    setError(null);
    setReady(false);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("Your browser doesn't support in-app camera. Tap 'From gallery' instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 1920 },
          height: { ideal: 1440 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setReady(true);
      }
    } catch (e) {
      // NotAllowedError, NotFoundError, NotReadableError, SecurityError, ...
      const msg =
        e?.name === "NotAllowedError"
          ? "Camera permission blocked. Enable it in your browser settings, or tap 'From gallery'."
          : e?.name === "NotFoundError"
          ? "No camera detected. Tap 'From gallery' to upload from your device."
          : "Couldn't start the camera. Tap 'From gallery' instead.";
      setError(msg);
    }
  }, []);

  // Start / stop on open
  useEffect(() => {
    if (!open) {
      stopStream();
      setShots([]);
      setError(null);
      return;
    }
    startStream(facingMode);
    return stopStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Swap facing (front/rear)
  useEffect(() => {
    if (!open) return;
    stopStream();
    startStream(facingMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode]);

  const takeShot = useCallback(async () => {
    if (!ready || !videoRef.current) return;
    if (guided && shots.length >= guidedLimit) return;
    if (!guided && shots.length >= maxShots) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    // Downscale to a max 1920px edge to keep upload payload small.
    const scale = Math.min(1, 1920 / Math.max(w, h));
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setFlash(true);
    setTimeout(() => setFlash(false), 120);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) return;
    const file = new File([blob], `floormatch-${Date.now()}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
    const shotKey = guided ? GUIDED_SHOTS[shots.length]?.key : null;
    if (shotKey) file.shotKey = shotKey; // read on the receiving side
    setShots((s) => [...s, { dataUrl: URL.createObjectURL(blob), file, key: shotKey }]);
  }, [ready, shots.length, maxShots, guided, guidedLimit]);

  const retakeAll = useCallback(() => {
    shots.forEach((s) => URL.revokeObjectURL(s.dataUrl));
    setShots([]);
  }, [shots]);

  const removeShot = useCallback(
    (idx) => {
      const removed = shots[idx];
      if (removed) URL.revokeObjectURL(removed.dataUrl);
      setShots((s) => s.filter((_, i) => i !== idx));
    },
    [shots],
  );

  const finish = useCallback(() => {
    if (shots.length === 0) return;
    onCapture(shots.map((s) => s.file));
    onClose();
  }, [shots, onCapture, onClose]);

  const openNative = useCallback(() => {
    nativeInputRef.current?.click();
  }, []);

  const onNativePicked = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []).slice(0, maxShots - shots.length);
      if (!files.length) return;
      onCapture(files);
      onClose();
    },
    [maxShots, shots.length, onCapture, onClose],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black text-white flex flex-col"
      data-testid="camera-capture"
      role="dialog"
      aria-modal="true"
      aria-label="Take a photo"
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/60 backdrop-blur-sm shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20"
          aria-label="Close camera"
          data-testid="camera-close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-sm font-semibold">
          {guided
            ? `${Math.min(shots.length + 1, guidedLimit)} of ${guidedLimit}`
            : `${shots.length} / ${maxShots} photos`}
        </div>
        <button
          type="button"
          onClick={() =>
            setFacingMode((m) => (m === "environment" ? "user" : "environment"))
          }
          className="p-2 rounded-full bg-white/10 hover:bg-white/20"
          aria-label="Switch camera"
          data-testid="camera-switch"
        >
          <RotateCw className="w-5 h-5" />
        </button>
      </div>

      {/* Live preview */}
      <div className="relative flex-1 min-h-0 overflow-hidden bg-black">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          playsInline
          muted
          autoPlay
          data-testid="camera-video"
        />
        <canvas ref={canvasRef} className="hidden" />
        {flash && (
          <div className="absolute inset-0 bg-white/70 pointer-events-none animate-pulse" aria-hidden="true" />
        )}
        {!ready && !error && (
          <div className="absolute inset-0 grid place-items-center text-white/70 text-sm">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 animate-pulse" /> Warming up camera…
            </div>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 grid place-items-center p-6 text-center">
            <div>
              <p className="text-white/90 text-sm max-w-xs mx-auto" data-testid="camera-error">
                {error}
              </p>
              {fallbackToPicker && (
                <button
                  type="button"
                  onClick={openNative}
                  className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-slate-900 font-semibold text-sm"
                  data-testid="camera-fallback-picker"
                >
                  <Images className="w-4 h-4" /> Pick from gallery
                </button>
              )}
            </div>
          </div>
        )}

        {/* Shot guide — either the freestyle coach-mark or the guided
            silhouette + caption. */}
        {ready && !error && (
          <>
            {guided && currentGuide && (
              <ShotSilhouette frame={currentGuide.frame} />
            )}
            <div className="absolute top-4 inset-x-4 sm:inset-x-1/4 pointer-events-none">
              <div className="bg-black/60 backdrop-blur-sm text-white text-xs sm:text-sm px-3 py-2 rounded-lg text-center">
                {guided && currentGuide ? (
                  <>
                    <div className="text-[10px] uppercase tracking-wider text-[#ffb078] font-semibold mb-0.5">
                      {currentGuide.caption}
                    </div>
                    <div>{currentGuide.hint}</div>
                  </>
                ) : guided ? (
                  <div>All 3 shots captured. Tap Use to continue.</div>
                ) : shots.length === 0 ? (
                  "Fill the frame with the roll or pack. Steady, good light."
                ) : (
                  `${shots.length} shot${shots.length === 1 ? "" : "s"} captured — tap the shutter for more, or Use to continue.`
                )}
              </div>
            </div>
          </>
        )}

        {/* Thumbnail film-strip of taken shots */}
        {shots.length > 0 && (
          <div
            className="absolute left-3 bottom-3 flex gap-2 max-w-[70%] overflow-x-auto pb-1"
            data-testid="camera-thumbs"
          >
            {shots.map((s, i) => (
              <button
                key={s.dataUrl}
                type="button"
                onClick={() => removeShot(i)}
                className="relative shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 border-white/80 group"
                aria-label={`Remove photo ${i + 1}`}
                data-testid={`camera-thumb-${i}`}
              >
                <img src={s.dataUrl} alt="" className="w-full h-full object-cover" />
                <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 grid place-items-center transition-opacity">
                  <X className="w-4 h-4 text-white" />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Shutter bar */}
      <div className="px-6 py-5 bg-black/70 backdrop-blur-sm shrink-0 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={openNative}
          className="text-white/90 hover:text-white inline-flex items-center gap-1.5 text-xs font-semibold"
          data-testid="camera-open-gallery"
        >
          <Images className="w-4 h-4" /> Gallery
        </button>

        <button
          type="button"
          onClick={takeShot}
          disabled={!ready || shots.length >= guidedLimit}
          aria-label="Take photo"
          className="relative w-20 h-20 rounded-full bg-white grid place-items-center disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-transform"
          data-testid="camera-shutter"
        >
          <span className="w-16 h-16 rounded-full border-4 border-slate-900" />
          <span className="absolute inset-0 rounded-full ring-4 ring-[#E05915]/70" aria-hidden="true" />
        </button>

        <button
          type="button"
          onClick={shots.length ? finish : onClose}
          disabled={shots.length === 0}
          className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-semibold transition-colors ${
            shots.length
              ? "bg-[#E05915] hover:bg-[#c24b10] text-white"
              : "bg-white/10 text-white/50 cursor-not-allowed"
          }`}
          data-testid="camera-use-shots"
        >
          <Check className="w-4 h-4" />
          {guided
            ? shots.length === guidedLimit
              ? "Finish & scan"
              : `Use ${shots.length || ""}`
            : `Use ${shots.length || ""}`}
        </button>
      </div>

      {shots.length > 0 && (
        <button
          type="button"
          onClick={retakeAll}
          className="absolute top-16 right-4 text-xs text-white/70 hover:text-white underline"
          data-testid="camera-retake-all"
        >
          Retake all
        </button>
      )}

      {/* Hidden native fallback (also used for desktop where getUserMedia
          isn't ideal). `capture="environment"` opens the mobile camera app
          on iOS/Android; on desktop it just falls back to a normal file
          picker. */}
      <input
        ref={nativeInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={onNativePicked}
        data-testid="camera-native-input"
      />
    </div>
  );
}

/**
 * Translucent silhouette overlay that shows the retailer where to frame
 * each shot in guided mode. Uses a soft outer mask so the guide feels
 * like a viewfinder, not a nag.
 */
function ShotSilhouette({ frame }) {
  const style = FRAME_STYLES[frame] || FRAME_STYLES.rectangle;
  return (
    <div className="absolute inset-0 pointer-events-none grid place-items-center">
      <div
        className={`border-2 border-dashed border-white/70 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.35)] ${style}`}
      >
        {frame === "label" && (
          <div className="absolute inset-0 grid place-items-center">
            <span className="inline-flex items-center gap-1.5 bg-[#E05915] text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full shadow">
              <Sparkles className="w-3 h-3" /> AI will read this
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

const FRAME_STYLES = {
  "rectangle-wide": "w-[85%] h-[45%]",
  square: "w-[70%] aspect-square",
  label: "w-[80%] h-[30%] relative",
  rectangle: "w-[80%] h-[55%]",
};
